import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Mail, MapPin, Building, User, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { Pagination } from '../components/ui/Pagination';
import { EmployeeFormModal } from '../components/EmployeeFormModal';
import { getEmployees, addEmployee, updateEmployee, deleteEmployee, getCompanies } from '../utils/companyManagement';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient } from '../lib/supabase';
export function EmployeeList() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('Todas');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const {
    addLog
  } = useActivityLog();
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const companies = getCompanies();
  const companyNames = ['Todas', ...companies.map(c => c.name)];
  // Load employees from Supabase
  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };
  // Load employees on mount
  useEffect(() => {
    loadEmployees();
  }, []);
  // Sincronización en tiempo real
  useEffect(() => {
    const subscription = supabaseClient.subscribeToEmployees(() => {
      console.log('Employee updated, reloading...');
      loadEmployees();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const handleAddEmployee = async (newEmployee: any) => {
    try {
      if (editingEmployee) {
        // Update existing
        await updateEmployee(editingEmployee.id, newEmployee);
        addLog('Empleado Actualizado', `${newEmployee.name} - ${companies.find(c => c.id === newEmployee.companyId)?.name}`, 'employee');
      } else {
        // Add new
        const employee = {
          ...newEmployee,
          id: `emp-${Date.now()}`
        };
        await addEmployee(employee);
        addLog('Empleado Creado', `${newEmployee.name} - ${companies.find(c => c.id === newEmployee.companyId)?.name}`, 'employee');
      }
      // Reload employees after changes
      await loadEmployees();
      setEditingEmployee(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };
  const handleEdit = (employee: any) => {
    setEditingEmployee(employee);
    setIsModalOpen(true);
  };
  const handleDelete = (employee: any) => {
    setDeleteConfirm(employee);
  };
  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteEmployee(deleteConfirm.id);
        addLog('Empleado Eliminado', `${deleteConfirm.name} - ${companies.find(c => c.id === deleteConfirm.companyId)?.name}`, 'employee');
        setDeleteConfirm(null);
        // Reload employees after deletion
        await loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };
  const handleOpenModal = () => {
    // Check if there are companies before opening modal
    if (companies.length === 0) {
      alert('⚠️ No hay empresas registradas. Por favor, crea una empresa primero antes de agregar empleados.');
      return;
    }
    setEditingEmployee(null);
    setIsModalOpen(true);
  };
  const filteredEmployees = employees.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) || e.email.toLowerCase().includes(searchTerm.toLowerCase()) || e.username && e.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = selectedCompany === 'Todas' || companies.find(c => c.id === e.companyId)?.name === selectedCompany;
    return matchesSearch && matchesCompany;
  });
  // Paginación
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginatedEmployees = filteredEmployees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany]);
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-1136">
        <div className="flex items-center justify-center py-20" data-id="element-1137">
          <div className="text-center" data-id="element-1138">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" data-id="element-1139"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-1140">
              Cargando empleados...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-1141">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-1142">
        <div data-id="element-1143">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1144">
            Directorio de Empleados
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-1145">
            Gestiona el acceso y perfiles de los empleados
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-1147" />} onClick={handleOpenModal} data-id="element-1146">
          Nuevo Empleado
        </Button>
      </div>

      {/* Warning if no companies */}
      {companies.length === 0 && <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3" data-id="element-1148">
          <AlertCircle className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" size={20} data-id="element-1149" />
          <div data-id="element-1150">
            <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100" data-id="element-1151">
              No hay empresas registradas
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1" data-id="element-1152">
              Debes crear al menos una empresa antes de poder agregar empleados.
            </p>
          </div>
        </div>}

      <div className="flex flex-col sm:flex-row items-end gap-4 mb-6" data-id="element-1153">
        <div className="w-full sm:flex-1" data-id="element-1154">
          <SearchBar placeholder="Buscar empleado..." className="w-full" onSearch={setSearchTerm} data-id="element-1155" />
        </div>

        <FilterDropdown label="Empresa" value={selectedCompany} options={companyNames} onChange={setSelectedCompany} className="w-full sm:w-48" data-id="element-1156" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1157">
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-1158">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1159">
            Total Empleados
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1160">
            {employees.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-1161">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1162">
            Activos
          </p>
          <p className="text-2xl font-bold text-green-600" data-id="element-1163">
            {employees.filter(e => e.status === 'Active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-1164">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-1165">
            Empresas
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1166">
            {new Set(employees.map(e => e.companyId)).size}
          </p>
        </div>
      </div>

      {filteredEmployees.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-1167">
          <User size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1168" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1169">
            No se encontraron empleados
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-1170">
            {searchTerm || selectedCompany !== 'Todas' ? 'Intenta con otros filtros' : 'Agrega tu primer empleado'}
          </p>
        </div> : <>
          <Card noPadding className="overflow-hidden" data-id="element-1171">
            <div className="overflow-x-auto" data-id="element-1172">
              <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-1173">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-1174">
                  <tr data-id="element-1175">
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1176">
                      Empleado
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1177">
                      Usuario
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1178">
                      Rol / Cargo
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1179">
                      Ubicación
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1180">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-1181">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900" data-id="element-1182">
                  {paginatedEmployees.map(employee => {
                const company = companies.find(c => c.id === employee.companyId);
                const branch = company?.branches.find(b => b.id === employee.branchId);
                return <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-1183">
                        <td className="px-6 py-4" data-id="element-1184">
                          <div className="flex items-center" data-id="element-1185">
                            <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-sm shrink-0" data-id="element-1186">
                              {employee.name.charAt(0)}
                            </div>
                            <div className="ml-4" data-id="element-1187">
                              <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1188">
                                {employee.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-1189">
                                <Mail size={12} data-id="element-1190" /> {employee.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1191">
                          <div className="text-sm text-gray-900 dark:text-white font-mono" data-id="element-1192">
                            {employee.username || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1193">
                          <div className="text-sm text-gray-900 dark:text-white" data-id="element-1194">
                            {employee.position}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1195">
                          <div className="text-sm text-gray-900 dark:text-white flex items-center gap-1" data-id="element-1196">
                            <Building size={14} className="text-gray-400" data-id="element-1197" />{' '}
                            {company?.name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5" data-id="element-1198">
                            <MapPin size={12} data-id="element-1199" /> {branch?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1200">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.status === 'Active' ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'}`} data-id="element-1201">
                            {employee.status === 'Active' ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-1202">
                          <div className="flex items-center justify-end gap-2" data-id="element-1203">
                            <button onClick={() => handleEdit(employee)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-1" title="Editar" data-id="element-1204">
                              <Edit2 size={16} data-id="element-1205" />
                            </button>
                            <button onClick={() => handleDelete(employee)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1" title="Eliminar" data-id="element-1206">
                              <Trash2 size={16} data-id="element-1207" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Paginación */}
          {totalPages > 1 && <div className="mt-4" data-id="element-1208">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredEmployees.length} itemsPerPage={itemsPerPage} data-id="element-1209" />
            </div>}
        </>}

      {/* Modals */}
      <EmployeeFormModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingEmployee(null);
    }} onSave={handleAddEmployee} editingEmployee={editingEmployee} data-id="element-1210" />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-1211">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-1212">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1213">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1214">
                Eliminar Empleado
              </h2>
            </div>
            <div className="p-6" data-id="element-1215">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-1216">
                ¿Estás seguro que deseas eliminar a{' '}
                <strong data-id="element-1217">{deleteConfirm.name}</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-1218">
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1219">
                  <strong data-id="element-1220">Email:</strong> {deleteConfirm.email}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1221">
                  <strong data-id="element-1222">Cargo:</strong> {deleteConfirm.position}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-1223">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-1224">
                Cancelar
              </Button>
              <Button onClick={confirmDelete} fullWidth className="bg-red-600 hover:bg-red-700" data-id="element-1225">
                Eliminar Empleado
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}